import { useState, useEffect, useCallback } from 'react';
import { Download, Loader, AlertCircle } from 'lucide-react';
import { getProxiedStorageUrl } from '@/lib/storageProxy';
import { triggerDownload } from '@/utils/download';
import { ViewerShell } from './ViewerShell';
import type { FavoriteCachedData } from '@/types/favorites';

interface CSVViewerProps {
  url: string;
  title?: string;
  fileName?: string;
  onClose: () => void;
  onDownload?: () => void;
  fileId?: string;
  favoriteCachedData?: FavoriteCachedData;
}

interface ParsedCSV {
  headers: string[];
  rows: string[][];
}

const ROWS_PER_PAGE = 100;

export function CSVViewer({
  url,
  title,
  fileName,
  onClose,
  onDownload,
  fileId,
  favoriteCachedData,
}: CSVViewerProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ParsedCSV | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const fetchAndParseCSV = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(getProxiedStorageUrl(url), { signal: controller.signal });
        if (!response.ok) throw new Error(`Failed to fetch CSV: ${response.status}`);
        const text = await response.text();
        const parsed = parseCSV(text);
        if (isMounted) setData(parsed);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('Failed to load CSV:', err);
        if (isMounted) setError(err instanceof Error ? err.message : 'Failed to load CSV file');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchAndParseCSV();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [url]);

  const totalPages = data ? Math.ceil(data.rows.length / ROWS_PER_PAGE) : 0;
  const startRow = (currentPage - 1) * ROWS_PER_PAGE;
  const endRow = Math.min(startRow + ROWS_PER_PAGE, data?.rows.length || 0);
  const displayedRows = data?.rows.slice(startRow, endRow) || [];

  const handlePageChange = useCallback(
    (page: number) => setCurrentPage(Math.max(1, Math.min(page, totalPages))),
    [totalPages]
  );

  const handleDownload = useCallback(() => {
    if (onDownload) onDownload();
    else triggerDownload(url, fileName || title || 'data.csv');
  }, [url, fileName, title, onDownload]);

  const subtitle = data ? `${data.headers.length} cols • ${data.rows.length} rows` : undefined;

  return (
    <ViewerShell
      title={title || (subtitle ? `CSV Data — ${subtitle}` : 'CSV Data')}
      type="doc"
      fileId={fileId}
      favoriteCachedData={favoriteCachedData}
      onClose={onClose}
      onDownload={handleDownload}
      currentPage={totalPages > 1 ? currentPage : undefined}
      totalPages={totalPages > 1 ? totalPages : undefined}
      onPageChange={totalPages > 1 ? handlePageChange : undefined}
      keyboardHint="Arrow keys to navigate pages • Esc to close"
    >
      <div className="relative w-full h-full min-h-full bg-white">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#faf8f5]">
            <div className="text-center">
              <Loader className="animate-spin text-[#8e4585] mx-auto" size={28} />
              <p className="text-[#5a3d58] mt-3 text-sm">Loading CSV data...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#faf8f5]">
            <div className="text-center max-w-md p-6">
              <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
              <h3 className="text-base font-medium text-[#5a3d58] mb-2">Cannot load CSV file</h3>
              <p className="text-[#8e4585]/60 text-sm mb-4">{error}</p>
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-[#8e4585] text-white rounded-lg hover:bg-[#753a6f] transition-colors flex items-center gap-2 mx-auto text-sm"
              >
                <Download size={16} /> Download instead
              </button>
            </div>
          </div>
        )}

        {data && !loading && !error && (
          <div className="h-full overflow-auto">
            <table className="w-full border-collapse text-xs sm:text-sm">
              <thead className="bg-[#fcf4fc] sticky top-0">
                <tr>
                  <th className="px-2 sm:px-3 py-2 text-left font-medium text-[#8e4585]/60 border-b border-[#f0d2f0]/30 w-10">
                    #
                  </th>
                  {data.headers.map((header, i) => (
                    <th
                      key={header || `header-${i}`}
                      className="px-2 sm:px-3 py-2 text-left font-medium text-[#5a3d58] border-b border-[#f0d2f0]/30 whitespace-nowrap"
                    >
                      {header || `Column ${i + 1}`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedRows.map((row, ri) => (
                  <tr key={startRow + ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-[#faf8f5]'}>
                    <td className="px-2 sm:px-3 py-2 text-[#8e4585]/40 border-b border-[#f0d2f0]/20 font-mono text-[10px]">
                      {startRow + ri + 1}
                    </td>
                    {data.headers.map((_, ci) => (
                      <td
                        key={ci}
                        className="px-2 sm:px-3 py-2 text-[#5a3d58] border-b border-[#f0d2f0]/20 max-w-[200px] sm:max-w-md"
                      >
                        <div className="truncate" title={row[ci] || ''}>
                          {row[ci] || ''}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ViewerShell>
  );
}

interface ParseState {
  lines: string[][];
  currentLine: string[];
  currentField: string;
  inQuotes: boolean;
}

function processQuotedChar(
  char: string,
  nextChar: string,
  state: ParseState
): { state: ParseState; skipNext: boolean } {
  if (char === '"') {
    if (nextChar === '"') {
      return {
        state: { ...state, currentField: state.currentField + '"' },
        skipNext: true,
      };
    }
    return { state: { ...state, inQuotes: false }, skipNext: false };
  }
  return {
    state: { ...state, currentField: state.currentField + char },
    skipNext: false,
  };
}

function finishCurrentField(state: ParseState): ParseState {
  return {
    ...state,
    currentLine: [...state.currentLine, state.currentField.trim()],
    currentField: '',
  };
}

function finishCurrentRow(state: ParseState): ParseState {
  const withField = {
    ...state,
    currentLine: [...state.currentLine, state.currentField.trim()],
    currentField: '',
  };
  const shouldPush =
    withField.currentLine.length > 0 || withField.currentLine.some((f) => f !== '');
  return {
    ...withField,
    lines: shouldPush ? [...state.lines, withField.currentLine] : state.lines,
    currentLine: [],
  };
}

function processUnquotedChar(
  char: string,
  nextChar: string,
  state: ParseState
): { state: ParseState; skipNext: boolean } {
  if (char === '"') {
    return { state: { ...state, inQuotes: true }, skipNext: false };
  }
  if (char === ',') {
    return { state: finishCurrentField(state), skipNext: false };
  }
  if (char === '\r') {
    const next = finishCurrentRow(state);
    return { state: next, skipNext: nextChar === '\n' };
  }
  if (char === '\n') {
    return { state: finishCurrentRow(state), skipNext: false };
  }
  return {
    state: { ...state, currentField: state.currentField + char },
    skipNext: false,
  };
}

function parseCSV(text: string): ParsedCSV {
  let state: ParseState = {
    lines: [],
    currentLine: [],
    currentField: '',
    inQuotes: false,
  };

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (state.inQuotes) {
      const result = processQuotedChar(char, nextChar, state);
      state = result.state;
      if (result.skipNext) i++;
    } else {
      const result = processUnquotedChar(char, nextChar, state);
      state = result.state;
      if (result.skipNext) i++;
    }
  }

  if (state.currentField || state.currentLine.length > 0) {
    state = {
      ...state,
      currentLine: [...state.currentLine, state.currentField.trim()],
    };
    if (state.currentLine.some((f) => f !== '')) {
      state = { ...state, lines: [...state.lines, state.currentLine] };
    }
  }

  const headers = state.lines[0] || [];
  const rows = state.lines.slice(1);
  const normalizedRows = rows.map((row) => {
    const normalized = [...row];
    while (normalized.length < headers.length) normalized.push('');
    return normalized.slice(0, headers.length);
  });

  return { headers, rows: normalizedRows };
}
